import { definePreset, palette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * Preset PrimeNG (design tokens/Aura) que reproduz a identidade visual da Blendus.
 *
 * Este arquivo e a traducao do antigo `theme-light.css` (styled-mode do PrimeNG
 * pre-18, sabor Material, com o primario trocado por #00bfeb) para o sistema de
 * design tokens do PrimeNG 21. As 1.400 regras daquele arquivo repetiam um
 * punhado de constantes - sao elas que estao aqui.
 *
 * Duplicado em @lib/ui/theme/blendus-preset (gestor-web nao consome libs/*).
 *
 * Referencia de conversao: `html { font-size: 14px }` (layout-light.css), entao
 * 1rem = 14px em todos os valores herdados do tema antigo.
 */

const PRIMARIA = '#00bfeb';
const TEXTO = 'rgba(0, 0, 0, 0.85)';
const TEXTO_SECUNDARIO = 'rgba(0, 0, 0, 0.6)';
const BRANCO = '#ffffff';
const ERRO = '#b00020';

/** Realce ao passar o mouse / linha selecionada - o cinza translucido do Material. */
const HOVER = 'rgba(0, 0, 0, 0.05)';
/** Divisorias internas (cabecalho de lista, bordas de painel). */
const BORDA_CONTEUDO = 'rgba(0, 0, 0, 0.1)';
/** Borda dos campos de formulario em repouso. */
const BORDA_CAMPO = 'rgba(0, 0, 0, 0.4)';
/** Faixa azul clara do item selecionado em listas e do chip de multiselect. */
const REALCE = 'rgba(0, 191, 235, 0.1)';
const REALCE_FOCO = 'rgba(0, 191, 235, 0.15)';

// Elevacoes do Material usadas pelo tema antigo, por nivel.
const ELEVACAO_4 = '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.15), 0 1px 10px 0 rgba(0, 0, 0, 0.1)';
const ELEVACAO_8 = '0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.15), 0 3px 14px 2px rgba(0, 0, 0, 0.1)';
const ELEVACAO_24 =
  '0 11px 15px -7px rgba(0, 0, 0, 0.2), 0 24px 38px 3px rgba(0, 0, 0, 0.15), 0 9px 46px 8px rgba(0, 0, 0, 0.1)';

export const BlendusPreset = definePreset(Aura, {
  primitive: {
    // o tema antigo usava 4px em praticamente tudo (60 das 60 ocorrencias que nao
    // sao 50% ou 0); o Aura escalona 4/6/8/12px conforme o componente.
    borderRadius: {
      sm: '4px',
      md: '4px',
      lg: '4px',
      xl: '4px',
    },
  },
  semantic: {
    primary: palette(PRIMARIA),
    transitionDuration: '0.2s',
    // `.p-disabled, .p-component:disabled { opacity: .38 }` era como o tema antigo
    // marcava TODO estado desabilitado, preservando as cores do estado (inclusive
    // o azul de um checkbox marcado). O Aura usa 0.6 e ainda repinta a caixa.
    disabledOpacity: '0.4',
    // o tema antigo nao desenhava anel de foco: `.p-button:focus { box-shadow: none }`.
    // Nos campos o foco e so a borda azul (mais o anel interno, na folha de identidade).
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: 'none',
    },
    formField: {
      // `.p-inputtext { padding: 0.75rem 0.75rem }` - o mesmo nos dois eixos.
      paddingX: '0.75rem',
      paddingY: '0.75rem',
      // idem no tamanho "small" (o tema usava 9.2px nos dois eixos; arredondado).
      // `fontSize` fica de fora: o padrao do Aura (0.875rem) ja e o do tema antigo.
      sm: {
        paddingX: '0.65rem',
        paddingY: '0.65rem',
      },
      // sem bloco `lg`: nenhum campo nem botao do app usa size="large"
      // (101 usos de "small", zero de "large"), entao seria config morta.

      // O tema antigo marcava o campo em foco com a borda azul MAIS um anel
      // interno, e repetia esse anel QUATRO vezes - as sombras se somam e o traco
      // fica visivelmente mais denso que um unico inset de 1px. E esse par
      // borda+anel adensado que o usuario reconhece como "campo em foco".
      focusRing: {
        shadow: `inset 0 0 0 1px ${PRIMARIA}, inset 0 0 0 1px ${PRIMARIA}, inset 0 0 0 1px ${PRIMARIA}, inset 0 0 0 1px ${PRIMARIA}`,
      },
    },
    // Listas de overlay (select, multiselect, autocomplete, listbox). O tema antigo
    // nao recuava as opcoes - o realce ia de ponta a ponta, com 0.75rem de padding
    // tanto no item quanto no cabecalho do filtro. O Aura recua a opcao (padding e
    // gap na lista + borderRadius no item), o que deixa o realce "flutuando" e faz a
    // faixa destacada mudar de largura conforme o estado.
    list: {
      padding: '0',
      gap: '0',
      header: {
        padding: '0.75rem',
      },
      option: {
        padding: '0.75rem',
        borderRadius: '0',
      },
    },
    navigation: {
      list: { padding: '0', gap: '0' },
      item: { padding: '0.75rem', borderRadius: '0' },
    },
    overlay: {
      // `.p-dropdown-panel` / `.p-multiselect-panel`: sem borda, raio 4px, elevacao 8.
      select: { borderRadius: '4px', shadow: ELEVACAO_8 },
      // Atencao: `overlay.popover` NAO alimenta so o Popover - o Aura o usa tambem
      // no painel do DatePicker, no overlay de filtro da tabela, no Toast, no
      // ColorPicker e no Tooltip. O tema antigo dava elevacao 8 a esse grupo e
      // reservava a 24 (e o padding maior) ao OverlayPanel; por isso o valor
      // compartilhado fica na elevacao 8 e o Popover se destaca por token proprio.
      popover: { borderRadius: '4px', padding: '0.75rem', shadow: ELEVACAO_8 },
      // `.p-dialog`: elevacao 24, cabecalho/conteudo com 1.25rem.
      modal: { borderRadius: '4px', padding: '1.25rem', shadow: ELEVACAO_24 },
      navigation: { shadow: ELEVACAO_4 },
    },
    colorScheme: {
      light: {
        primary: {
          color: PRIMARIA,
          contrastColor: BRANCO,
          // o tema antigo escurecia o botao por opacidade, nao trocando de tom
          // (hover .92 e active .68 sobre o primario; arredondados para 0.9 e 0.7)
          hoverColor: 'rgba(0, 191, 235, 0.9)',
          activeColor: 'rgba(0, 191, 235, 0.7)',
        },
        highlight: {
          background: REALCE,
          focusBackground: REALCE_FOCO,
          color: PRIMARIA,
          focusColor: PRIMARIA,
        },
        mask: {
          background: 'rgba(0, 0, 0, 0.3)',
        },
        formField: {
          background: BRANCO,
          // o tema antigo nao mudava o fundo do campo desabilitado: so esmaecia o
          // componente inteiro (ver disabledOpacity acima).
          disabledBackground: BRANCO,
          filledBackground: '#f5f5f5',
          filledHoverBackground: '#ececec',
          filledFocusBackground: '#f5f5f5',
          borderColor: BORDA_CAMPO,
          hoverBorderColor: TEXTO,
          focusBorderColor: PRIMARIA,
          invalidBorderColor: ERRO,
          color: TEXTO,
          disabledColor: TEXTO,
          placeholderColor: TEXTO_SECUNDARIO,
          invalidPlaceholderColor: ERRO,
          floatLabelColor: TEXTO_SECUNDARIO,
          floatLabelFocusColor: PRIMARIA,
          floatLabelActiveColor: TEXTO_SECUNDARIO,
          floatLabelInvalidColor: ERRO,
          iconColor: TEXTO_SECUNDARIO,
          // o tema antigo nao tinha sombra em campo de formulario.
          shadow: 'none',
        },
        text: {
          color: TEXTO,
          hoverColor: TEXTO,
          mutedColor: TEXTO_SECUNDARIO,
          hoverMutedColor: TEXTO_SECUNDARIO,
        },
        content: {
          background: BRANCO,
          hoverBackground: HOVER,
          borderColor: BORDA_CONTEUDO,
          color: TEXTO,
          hoverColor: TEXTO,
        },
        overlay: {
          // `border: 0 none` nos tres: o recorte vem da sombra, nao de uma linha.
          select: { background: BRANCO, borderColor: 'transparent', color: TEXTO },
          popover: { background: BRANCO, borderColor: 'transparent', color: TEXTO },
          modal: { background: BRANCO, borderColor: 'transparent', color: TEXTO },
        },
        list: {
          option: {
            focusBackground: HOVER,
            selectedBackground: REALCE,
            selectedFocusBackground: REALCE_FOCO,
            color: TEXTO,
            focusColor: TEXTO,
            selectedColor: PRIMARIA,
            selectedFocusColor: PRIMARIA,
            icon: { color: TEXTO_SECUNDARIO, focusColor: TEXTO_SECUNDARIO },
          },
          optionGroup: { background: 'transparent', color: TEXTO_SECUNDARIO },
        },
        navigation: {
          item: {
            focusBackground: HOVER,
            activeBackground: HOVER,
            color: TEXTO,
            focusColor: TEXTO,
            activeColor: TEXTO,
            icon: { color: TEXTO_SECUNDARIO, focusColor: TEXTO_SECUNDARIO, activeColor: TEXTO_SECUNDARIO },
          },
          submenuLabel: { background: 'transparent', color: TEXTO_SECUNDARIO },
          submenuIcon: { color: TEXTO_SECUNDARIO, focusColor: TEXTO_SECUNDARIO, activeColor: TEXTO_SECUNDARIO },
        },
      },
    },
  },
  components: {
    button: {
      root: {
        // O tema antigo escrevia `border: 0 none` no botao; o v21 desenha 1px de
        // borda, o que somaria 2px na altura e desalinharia as barras de filtro.
        // Anular a borda por CSS nao para de pe: o componente reaplica o SHORTHAND
        // `border: 1px solid ...` no hover e no active, e o botao cresceria sob o
        // mouse. Como nao ha token de largura de borda, o caminho e descontar 1px
        // do padding e deixar a borda existir - no botao preenchido ela e invisivel
        // (borderColor = background) e no outlined ela substitui, com a mesma
        // espessura, o `box-shadow: inset 0 0 0 1px` que o tema antigo usava.
        //   vertical:   9px   - 1px = 8px   -> 0.6rem (8.4px)
        //   horizontal: 10.5px - 1px = 9.5px -> 0.7rem (9.8px)
        // O vertical NAO acompanha o do input (0.75rem), por isso nao herda de
        // formField.
        paddingY: '0.6rem',
        paddingX: '0.7rem',
        // largura fixa, e o box-sizing e border-box: a borda cabe dentro
        iconOnlyWidth: '2.75rem',
        sm: {
          //   7.9px - 1px = 6.9px -> 0.5rem (7px)
          //   9.2px - 1px = 8.2px -> 0.6rem (8.4px)
          paddingY: '0.5rem',
          paddingX: '0.6rem',
        },
        label: { fontWeight: '500' },
      },
      colorScheme: {
        light: {
          // O tema antigo pintava hover/foco/ativo dos botoes sem preenchimento com
          // a cor do proprio botao em OPACIDADE BAIXA (4%, 12% e 16%). Sendo
          // translucido, o fundo atras aparece: numa linha de grid cinza ou azul o
          // realce quase nao se destaca. O Aura usa tons opacos ({primary.50} /
          // {surface.50}), que viram uma mancha clara por cima da linha.
          text: {
            primary: {
              hoverBackground: 'rgba(0, 191, 235, 0.05)',
              activeBackground: 'rgba(0, 191, 235, 0.15)',
            },
            secondary: {
              hoverBackground: 'rgba(148, 163, 184, 0.05)',
              activeBackground: 'rgba(148, 163, 184, 0.15)',
            },
          },
          outlined: {
            // o contorno do tema antigo era a propria cor do texto (`inset 0 0 0 1px`
            // herdando currentColor); o Aura usa o tom 200, bem mais claro
            primary: {
              borderColor: '{primary.color}',
              hoverBackground: 'rgba(0, 191, 235, 0.05)',
              activeBackground: 'rgba(0, 191, 235, 0.15)',
            },
            secondary: {
              borderColor: '{surface.500}',
              hoverBackground: 'rgba(148, 163, 184, 0.05)',
              activeBackground: 'rgba(148, 163, 184, 0.15)',
            },
          },
        },
      },
    },
    // `.p-overlaypanel`: o unico overlay do tema antigo com elevacao 24, e a camada
    // de extensoes ainda dobrava o padding do conteudo (0.75rem -> 1.5rem).
    popover: {
      root: { shadow: ELEVACAO_24 },
      content: { padding: '1.5rem' },
    },
    // O painel do DatePicker tinha padding proprio (0.5rem) e nenhuma borda; sem
    // isso herdaria o padding do grupo popover e ficaria com o dobro de folga.
    datepicker: {
      panel: { padding: '0.5rem', borderColor: 'transparent' },
      date: {
        // 2.25rem no tema antigo, contra 2rem do Aura
        width: '2.25rem',
        height: '2.25rem',
        // o dia/mes selecionado era azul CLARO com texto azul, nao azul solido com
        // texto branco (`.p-highlight { color: #00bfeb; background: rgba(0,191,235, 0.1) }`).
        // Vale para as duas visoes: a celula de mes usa os tokens de `date`.
        selectedBackground: REALCE,
        selectedColor: PRIMARIA,
      },
      month: {
        // 0.5rem (7px) no tema antigo, contra 0.375rem do Aura
        padding: '0.5rem',
      },
    },
    checkbox: {
      root: {
        // 18px exatos (o Aura usa 1.25rem = 17.5px). Em px, e nao rem, porque um valor
        // fracionario (19.6px) faz o antialiasing do circulo parecer levemente ovalado.
        width: '18px',
        height: '18px',
        // a camada de extensoes reduzia o raio da caixa de 4px para 2px
        borderRadius: '2px',
        borderColor: '#757575',
        hoverBorderColor: TEXTO,
        // marcado e desabilitado continua azul (so esmaecido pela opacidade),
        // como no tema antigo; o Aura devolveria a borda cinza do campo.
        checkedDisabledBorderColor: PRIMARIA,
      },
      icon: {
        // 14px
        size: '1rem',
        disabledColor: BRANCO,
      },
    },
    radiobutton: {
      root: {
        // 20px exatos - ver a nota no checkbox sobre px vs rem
        width: '20px',
        height: '20px',
        borderColor: '#757575',
        // ao contrario do checkbox, o radio nao escurecia a borda no hover
        // (a extensao repetia `border: 2px solid #757575` em :hover e :focus)
        hoverBorderColor: '#757575',
        // o tema antigo NAO preenchia o radio marcado: mantinha o fundo branco com
        // a borda azul, e so o pino interno era azul. O Aura preenche de primario e
        // pinta o pino de branco.
        checkedBackground: BRANCO,
        checkedHoverBackground: BRANCO,
        checkedDisabledBorderColor: PRIMARIA,
      },
      icon: {
        // 10px exatos, pelo mesmo motivo
        size: '10px',
        checkedColor: PRIMARIA,
        checkedHoverColor: PRIMARIA,
        disabledColor: PRIMARIA,
      },
    },
    // Ao flutuar, o rotulo ganha padding horizontal mas mantem o mesmo
    // inset-inline-start, entao o texto anda ~2px para a direita em vez de subir
    // reto. O tema antigo compensava com margem negativa (`padding: 2px 4px` +
    // `margin-left: -4px`); a margem, que nao tem token, vai na folha de identidade.
    floatlabel: {
      root: {
        active: {
          // o tema antigo usava 12px no rotulo flutuante; o Aura usa 0.75rem (10.5px)
          fontSize: '0.85rem',
        },
      },
      on: {
        active: {
          padding: '2px 4px',
        },
      },
    },
    // Balao escuro translucido, sem sombra e sem seta - a extensao escondia a seta
    // e trocava a sombra do grupo popover por nenhuma.
    tooltip: {
      root: {
        padding: '0.5rem',
        borderRadius: '4px',
        shadow: 'none',
        // nem o tema antigo nem o CSS base do v16 limitavam a largura; o Aura corta
        // em 12.5rem, o que quebra em duas linhas tooltips que antes cabiam numa so
        maxWidth: 'none',
      },
      colorScheme: {
        light: {
          root: {
            background: 'rgba(97, 97, 97, 0.9)',
            color: BRANCO,
          },
        },
      },
    },
    dialog: {
      title: {
        fontWeight: '500',
      },
      // Sem folga inferior no conteudo: e como todos os dialogos da aplicacao
      // sempre apareceram. No app antigo isso vinha por acidente - um ::ng-deep
      // sem :host no notificacao-dialog vazava para todo p-dialog e anulava o
      // padding de baixo do tema. A regra la foi escopada ao proprio dialogo;
      // aqui o mesmo resultado passa a ser uma decisao explicita.
      content: {
        padding: '0 1.25rem',
      },
      // `.p-dialog .p-dialog-footer { padding: 0.75rem 1.25rem }` no tema antigo.
      footer: {
        padding: '0.75rem 1.25rem',
      },
    },
    // Barra fina (4px), sem cantos, trilho no primario esmaecido; o rotulo de
    // porcentagem nao era exibido (escondido na folha de identidade).
    progressbar: {
      root: {
        height: '4px',
        borderRadius: '0',
        background: 'rgba(0, 191, 235, 0.3)',
      },
    },
    // O tema antigo mostrava os itens selecionados do MultiSelect como pill azul
    // claro com texto azul. Damos essa identidade ao Chip, que e o componente
    // usado agora para renderiza-los.
    chip: {
      root: {
        borderRadius: '4px',
        paddingX: '0.75rem',
        paddingY: '0.4rem',
      },
      colorScheme: {
        light: {
          root: {
            background: REALCE,
            color: PRIMARIA,
          },
        },
      },
    },
    // O Aura usa 1rem de padding vertical na aba; o tema antigo usava 0.75rem. A faixa
    // de abas ficava ~7px mais alta, o que empurra o grid para baixo nas telas cuja
    // altura e calculada com calc(100vh - Npx) e faz aparecer barra de rolagem vertical.
    tabs: {
      tab: {
        padding: '0.75rem 1.25rem',
      },
    },
    datatable: {
      headerCell: { padding: '0.75rem 0.75rem' },
      bodyCell: { padding: '0.75rem 0.75rem' },
      header: { padding: '0.75rem 0.75rem' },
      footer: { padding: '0.75rem 0.75rem' },
      footerCell: { padding: '0.75rem 0.75rem' },
    },
    // O tema antigo nao definia tipografia no Toast: herdava 1rem (14px) no resumo e
    // no detalhe, com o resumo em negrito 700. O Aura passou a definir detalhe em
    // 0.875rem e resumo em 500, deixando o texto visivelmente menor que o original.
    toast: {
      // o conteudo tinha padding proprio (1.25rem), nao o do grupo popover
      content: {
        padding: '1.25rem',
      },
      summary: {
        fontWeight: '700',
      },
      detail: {
        fontSize: '1rem',
      },
    },
    // O ToggleButton/SelectButton do tema antigo era todo em escala de cinza (fundo
    // branco, borda cinza clara, texto escuro, selecionado preenchido de cinza,
    // inclusive nos estados :focus). O azul que aparece na barra de "Visoes" dos
    // grids nunca foi o padrao: vem do CSS proprio do ag-views-toolbar. Aqui fica so
    // o PADRAO; o azul continua no componente que sempre foi dono dele.
    togglebutton: {
      // No v16 a opcao era um elemento so (.p-button, padding 0.643rem). No v21 sao
      // dois (root + content) e os paddings SOMAM, deixando o botao mais alto que os
      // demais filtros - por isso o root vai a zero e a altura vem so do content.
      root: {
        padding: '0',
        sm: {
          padding: '0',
        },
      },
      content: {
        padding: '0.6rem 0.75rem',
        sm: {
          padding: '0.65rem 0.75rem',
        },
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            checkedBackground: '{surface.200}',
            borderColor: '{surface.300}',
            checkedBorderColor: '{surface.200}',
            color: '{text.color}',
            checkedColor: '{text.color}',
            hoverBackground: '{surface.50}',
            hoverColor: '{text.color}',
          },
          content: {
            // sem a pilula interna: quem carrega o estado marcado e o proprio root
            checkedBackground: 'transparent',
          },
        },
      },
    },
  },
});
